import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { 
  extractVideoDuration, 
  extractImageDimensions,
  extractVideoDimensions,
  formatDuration, 
  calculateAspectRatio,
  aspectRatiosMatch,
  isSupportedVideoFormat, 
  isSupportedImageFormat,
  getAllowedFileTypes, 
  getFileAcceptString,
  generateImageThumbnail,
  generateVideoThumbnail,
  extractFileSize
} from '../../../utils/videoUtils';

const UploadModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  brands = [], 
  categories = [], 
  generateKvId 
}) => {
  const [formData, setFormData] = useState({
    title: '',
    brandId: '',
    categoryId: '',
    brandingType: '',
    widthFt: '',
    heightFt: '',
    file: null,
    durationSeconds: null,
    durationOverrideSeconds: '',
    metaWidthPx: null,
    metaHeightPx: null,
    metaAspect: null,
    thumbnailUrl: null
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [previewKvId, setPreviewKvId] = useState('');
  const [calculatedArea, setCalculatedArea] = useState(0);
  const [enteredAspect, setEnteredAspect] = useState(0);
  const [isExtractingMetadata, setIsExtractingMetadata] = useState(false);
  const [durationDisplay, setDurationDisplay] = useState('');
  const [aspectWarning, setAspectWarning] = useState('');

  const brandingTypeOptions = [
    { value: 'Billboard', label: 'Billboard' },
    { value: 'LED Screen', label: 'LED Screen' },
    { value: 'In-Store Static', label: 'In-Store Static' },
    { value: 'Gondola Branding', label: 'Gondola Branding' },
    { value: 'Window Display', label: 'Window Display References' }
  ];

  const brandOptions = brands?.map(brand => ({
    value: brand?.id,
    label: `${brand?.name} (${brand?.code})`
  }));

  const categoryOptions = categories?.map(category => ({
    value: category?.id,
    label: `${category?.name} (${category?.code})`
  }));

  useEffect(() => {
    if (formData?.brandId && formData?.categoryId) {
      const brand = brands?.find(b => b?.id === formData?.brandId);
      const category = categories?.find(c => c?.id === formData?.categoryId);
      if (brand && category) {
        setPreviewKvId(generateKvId(brand?.code, category?.code));
      }
    } else {
      setPreviewKvId('');
    }
  }, [formData?.brandId, formData?.categoryId, brands, categories, generateKvId]);

  // Auto-calculate area and entered aspect when width or height changes
  useEffect(() => {
    const width = parseFloat(formData?.widthFt) || 0;
    const height = parseFloat(formData?.heightFt) || 0;
    const area = width * height;
    const aspect = calculateAspectRatio(width, height);
    
    setCalculatedArea(area);
    setEnteredAspect(aspect);
  }, [formData?.widthFt, formData?.heightFt]);

  // Check aspect ratio compatibility and show warning
  useEffect(() => {
    if (formData?.metaAspect && enteredAspect && formData?.widthFt && formData?.heightFt) {
      if (!aspectRatiosMatch(formData?.metaAspect, enteredAspect)) {
        setAspectWarning('⚠ The entered physical dimensions do not match the media\'s aspect ratio. This may cause cropping or distortion.');
      } else {
        setAspectWarning('');
      }
    } else {
      setAspectWarning('');
    }
  }, [formData?.metaAspect, enteredAspect, formData?.widthFt, formData?.heightFt]);

  // Duration display calculation
  useEffect(() => {
    const finalDuration = formData?.durationOverrideSeconds 
      ? parseFloat(formData?.durationOverrideSeconds) 
      : formData?.durationSeconds;
      
    if (finalDuration && finalDuration > 0) {
      setDurationDisplay(formatDuration(finalDuration));
    } else {
      setDurationDisplay('');
    }
  }, [formData?.durationSeconds, formData?.durationOverrideSeconds]);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        title: '',
        brandId: '',
        categoryId: '',
        brandingType: '',
        widthFt: '',
        heightFt: '',
        file: null,
        durationSeconds: null,
        durationOverrideSeconds: '',
        metaWidthPx: null,
        metaHeightPx: null,
        metaAspect: null,
        thumbnailUrl: null
      });
      setErrors({});
      setPreviewKvId('');
      setCalculatedArea(0);
      setEnteredAspect(0);
      setDurationDisplay('');
      setAspectWarning('');
    }
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileChange = async (file) => {
    if (file) {
      const allowedTypes = getAllowedFileTypes(formData?.brandingType);
      
      // Enhanced validation for LED Screen - only videos
      if (formData?.brandingType === 'LED Screen') {
        if (!isSupportedVideoFormat(file)) {
          setErrors(prev => ({ ...prev, file: 'LED Screen only supports video files (MP4, MOV, AVI, MKV)' }));
          return;
        }
      } else if (!allowedTypes?.includes(file?.type)) {
        setErrors(prev => ({ ...prev, file: 'Please upload a valid image (JPG, PNG, WEBP) or video (MP4, MOV, AVI, MKV) file' }));
        return;
      }

      // Validate file size (50MB max)
      if (file?.size > 50 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, file: 'File size must be less than 50MB' }));
        return;
      }

      handleInputChange('file', file);

      // Extract metadata for all files
      setIsExtractingMetadata(true);
      
      try {
        let dimensions = null;
        let duration = null;
        let thumbnailUrl = null;

        // Extract dimensions and generate thumbnail based on file type
        if (file?.type?.startsWith('image/') && isSupportedImageFormat(file)) {
          dimensions = await extractImageDimensions(file);
          try {
            thumbnailUrl = await generateImageThumbnail(file);
            handleInputChange('thumbnailUrl', thumbnailUrl);
          } catch (thumbnailError) {
            console.warn('Could not generate image thumbnail:', thumbnailError);
          }
        } else if (file?.type?.startsWith('video/') && isSupportedVideoFormat(file)) {
          dimensions = await extractVideoDimensions(file);
          // Also extract duration for videos
          try {
            duration = await extractVideoDuration(file);
          } catch (durationError) {
            console.warn('Could not extract video duration:', durationError);
          }
          // Generate video thumbnail
          try {
            thumbnailUrl = await generateVideoThumbnail(file);
            handleInputChange('thumbnailUrl', thumbnailUrl);
          } catch (thumbnailError) {
            console.warn('Could not generate video thumbnail:', thumbnailError);
          }
        }

        if (dimensions) {
          const metaAspect = calculateAspectRatio(dimensions?.width, dimensions?.height);
          
          handleInputChange('metaWidthPx', dimensions?.width);
          handleInputChange('metaHeightPx', dimensions?.height);
          handleInputChange('metaAspect', metaAspect);
        }

        if (duration !== null) {
          handleInputChange('durationSeconds', duration);
        }

        // Clear any previous errors
        setErrors(prev => ({ ...prev, file: '', durationSeconds: '' }));
        
      } catch (error) {
        console.warn('Could not extract file metadata:', error);
        // Reset metadata if extraction fails
        handleInputChange('metaWidthPx', null);
        handleInputChange('metaHeightPx', null);
        handleInputChange('metaAspect', null);
        handleInputChange('thumbnailUrl', null);
        
        if (formData?.brandingType === 'LED Screen' && file?.type?.startsWith('video/')) {
          setErrors(prev => ({ ...prev, durationSeconds: 'Could not extract video duration automatically. Please enter manually.' }));
        }
      } finally {
        setIsExtractingMetadata(false);
      }
    }
  };

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFileChange(e?.dataTransfer?.files?.[0]);
    }
  };

  const requiresDimensions = (brandingType) => {
    return ['Billboard', 'LED Screen', 'In-Store Static', 'Gondola Branding']?.includes(brandingType);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.title?.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData?.brandId) {
      newErrors.brandId = 'Brand is required';
    }

    if (!formData?.categoryId) {
      newErrors.categoryId = 'Category is required';
    }

    if (!formData?.brandingType) {
      newErrors.brandingType = 'Branding type is required';
    }

    // Enhanced validation for dimensions on required branding types
    if (requiresDimensions(formData?.brandingType)) {
      if (!formData?.widthFt?.trim() || parseFloat(formData?.widthFt) <= 0) {
        newErrors.widthFt = 'Width is required for this branding type';
      }
      if (!formData?.heightFt?.trim() || parseFloat(formData?.heightFt) <= 0) {
        newErrors.heightFt = 'Height is required for this branding type';
      }
    }

    if (!formData?.file) {
      newErrors.file = 'Please select a file to upload';
    } else {
      // Additional validation for LED Screen
      if (formData?.brandingType === 'LED Screen' && !isSupportedVideoFormat(formData?.file)) {
        newErrors.file = 'LED Screen only supports video files (MP4, MOV, AVI, MKV)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Calculate final duration
      const finalDurationSeconds = formData?.durationOverrideSeconds 
        ? parseFloat(formData?.durationOverrideSeconds)
        : formData?.durationSeconds;

      // Extract and format file size
      const fileSizeFormatted = extractFileSize(formData?.file);

      // Transform data to include all new fields
      const submitData = {
        ...formData,
        widthFt: parseFloat(formData?.widthFt) || null,
        heightFt: parseFloat(formData?.heightFt) || null,
        areaSqft: calculatedArea || null,
        enteredAspect: enteredAspect || null,
        widthUnit: formData?.widthFt ? 'feet' : null,
        heightUnit: formData?.heightFt ? 'feet' : null,
        areaUnit: calculatedArea ? 'sqft' : null,
        durationSeconds: finalDurationSeconds || null,
        durationDisplay: finalDurationSeconds ? formatDuration(finalDurationSeconds) : null,
        fileSizeFormatted,
        fileExtension: formData?.file?.name?.split('.')?.pop()?.toUpperCase(),
        fileType: formData?.file?.type?.startsWith('image/') ? 'image' : 'video'
      };
      
      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Error uploading visual:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border shadow-elevated w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Upload" size={20} className="text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">Upload Key Visual</h2>
                <p className="text-sm text-muted-foreground">
                  Upload a new visual asset for branding campaigns
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4">Basic Information</h3>
            <div className="space-y-4">
              <Input
                label="Title"
                type="text"
                placeholder="Enter visual title"
                value={formData?.title}
                onChange={(e) => handleInputChange('title', e?.target?.value)}
                error={errors?.title}
                required
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Brand"
                  placeholder="Select brand"
                  options={brandOptions}
                  value={formData?.brandId}
                  onChange={(value) => handleInputChange('brandId', value)}
                  error={errors?.brandId}
                  required
                />
                <Select
                  label="Category"
                  placeholder="Select category"
                  options={categoryOptions}
                  value={formData?.categoryId}
                  onChange={(value) => handleInputChange('categoryId', value)}
                  error={errors?.categoryId}
                  required
                />
              </div>
            </div>
          </div>

          {/* KV ID Preview */}
          {previewKvId && (
            <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="Hash" size={16} className="text-primary" />
                <span className="text-sm font-medium text-primary">Generated KV ID:</span>
                <span className="text-lg font-bold text-primary font-mono">{previewKvId}</span>
              </div>
            </div>
          )}

          {/* Branding Details */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4">Branding Details</h3>
            <div className="space-y-4">
              <Select
                label="Branding Type"
                placeholder="Select branding type"
                options={brandingTypeOptions}
                value={formData?.brandingType}
                onChange={(value) => handleInputChange('brandingType', value)}
                error={errors?.brandingType}
                required
              />
              
              {/* Dimensions for required branding types */}
              {requiresDimensions(formData?.brandingType) && (
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-foreground">Physical Dimensions (Required)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Width (in feet)"
                      type="number"
                      placeholder="e.g., 20"
                      value={formData?.widthFt}
                      onChange={(e) => handleInputChange('widthFt', e?.target?.value)}
                      error={errors?.widthFt}
                      required
                      min="0.1"
                      step="0.1"
                    />
                    <Input
                      label="Height (in feet)"
                      type="number"
                      placeholder="e.g., 12"
                      value={formData?.heightFt}
                      onChange={(e) => handleInputChange('heightFt', e?.target?.value)}
                      error={errors?.heightFt}
                      required
                      min="0.1"
                      step="0.1"
                    />
                  </div>
                  
                  {/* Auto-calculated Area and Entered Aspect Display */}
                  {calculatedArea > 0 && (
                    <div className="p-3 bg-accent/10 border border-accent/20 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Icon name="Calculator" size={16} className="text-accent" />
                        <span className="text-sm font-medium text-accent">Area (sqft):</span>
                        <span className="text-lg font-bold text-accent">{calculatedArea?.toFixed(2)} sqft</span>
                        {enteredAspect > 0 && (
                          <>
                            <span className="text-sm font-medium text-accent ml-4">Entered Aspect:</span>
                            <span className="text-lg font-bold text-accent">{enteredAspect?.toFixed(3)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Optional Dimensions for Window Display */}
              {formData?.brandingType === 'Window Display' && (
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-foreground">Physical Dimensions (Optional)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Width (in feet)"
                      type="number"
                      placeholder="e.g., 15"
                      value={formData?.widthFt}
                      onChange={(e) => handleInputChange('widthFt', e?.target?.value)}
                      min="0.1"
                      step="0.1"
                    />
                    <Input
                      label="Height (in feet)"
                      type="number"
                      placeholder="e.g., 10"
                      value={formData?.heightFt}
                      onChange={(e) => handleInputChange('heightFt', e?.target?.value)}
                      min="0.1"
                      step="0.1"
                    />
                  </div>
                  
                  {calculatedArea > 0 && (
                    <div className="p-3 bg-accent/10 border border-accent/20 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Icon name="Calculator" size={16} className="text-accent" />
                        <span className="text-sm font-medium text-accent">Area (sqft):</span>
                        <span className="text-lg font-bold text-accent">{calculatedArea?.toFixed(2)} sqft</span>
                        {enteredAspect > 0 && (
                          <>
                            <span className="text-sm font-medium text-accent ml-4">Entered Aspect:</span>
                            <span className="text-lg font-bold text-accent">{enteredAspect?.toFixed(3)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* File Upload */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4">File Upload</h3>
            
            {/* File type notice for LED Screen */}
            {formData?.brandingType === 'LED Screen' && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <Icon name="Info" size={16} className="text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">
                    LED Screen supports only video files (MP4, MOV, AVI, MKV)
                  </span>
                </div>
              </div>
            )}
            
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                dragActive
                  ? 'border-primary bg-primary/5'
                  : errors?.file
                  ? 'border-destructive' :'border-border hover:border-primary/50'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {formData?.file ? (
                <div className="space-y-3">
                  {formData?.thumbnailUrl && (
                    <div className="flex justify-center">
                      <img 
                        src={formData?.thumbnailUrl} 
                        alt="Preview" 
                        className="max-w-48 max-h-32 rounded border border-border object-cover"
                      />
                    </div>
                  )}
                  <Icon name="CheckCircle" size={32} className="text-success mx-auto" />
                  <div>
                    <p className="font-medium text-foreground">{formData?.file?.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {formatFileSize(formData?.file?.size)} • {formData?.file?.type?.startsWith('image') ? 'Image' : 'Video'}
                    </p>
                    {isExtractingMetadata && (
                      <p className="text-xs text-blue-600">Extracting file metadata and generating thumbnail...</p>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleInputChange('file', null)}
                  >
                    Remove File
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Icon name="Upload" size={32} className="text-muted-foreground mx-auto" />
                  <div>
                    <p className="font-medium text-foreground">Drop files here or click to browse</p>
                    <p className="text-sm text-muted-foreground">
                      {formData?.brandingType === 'LED Screen' ? 'Supports MP4, MOV, AVI, MKV (Max 50MB)': 'Supports JPG, PNG, WEBP, MP4, MOV, AVI, MKV (Max 50MB)'
                      }
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('file-input')?.click()}
                  >
                    Choose File
                  </Button>
                  <input
                    id="file-input"
                    type="file"
                    accept={getFileAcceptString(formData?.brandingType)}
                    onChange={(e) => handleFileChange(e?.target?.files?.[0])}
                    className="hidden"
                  />
                </div>
              )}
            </div>
            {errors?.file && (
              <p className="text-sm text-destructive mt-2">{errors?.file}</p>
            )}
          </div>

          {/* Media Metadata Display */}
          {(formData?.metaWidthPx && formData?.metaHeightPx) && (
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Media Metadata</h3>
              <div className="space-y-4">
                {/* File Dimensions Display */}
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Icon name="Image" size={16} className="text-green-600" />
                    <span className="text-sm font-medium text-green-800">File Dimensions:</span>
                    <span className="text-lg font-bold text-green-800 font-mono">
                      {formData?.metaWidthPx} × {formData?.metaHeightPx}px
                    </span>
                    {formData?.metaAspect && (
                      <>
                        <span className="text-sm font-medium text-green-800 ml-4">Aspect Ratio:</span>
                        <span className="text-lg font-bold text-green-800">{formData?.metaAspect?.toFixed(3)}</span>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Read-only metadata fields */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    label="Meta_Width_px"
                    type="number"
                    value={formData?.metaWidthPx || ''}
                    readOnly
                    className="bg-gray-50"
                    helpText="Auto-extracted from file"
                  />
                  <Input
                    label="Meta_Height_px"
                    type="number"
                    value={formData?.metaHeightPx || ''}
                    readOnly
                    className="bg-gray-50"
                    helpText="Auto-extracted from file"
                  />
                  <Input
                    label="Meta_Aspect"
                    type="number"
                    value={formData?.metaAspect?.toFixed(6) || ''}
                    readOnly
                    className="bg-gray-50"
                    helpText="Calculated: Width/Height"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Aspect Ratio Comparison and Warning */}
          {aspectWarning && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={20} className="text-yellow-600 mt-0.5" />
                <div className="space-y-2">
                  <p className="text-sm font-medium text-yellow-800">{aspectWarning}</p>
                  
                  {/* Side-by-side comparison */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div className="p-3 bg-white border border-yellow-200 rounded">
                      <h5 className="text-sm font-medium text-yellow-800 mb-1">File Dimensions</h5>
                      <p className="text-xs text-yellow-700">
                        {formData?.metaWidthPx} × {formData?.metaHeightPx}px
                      </p>
                      <p className="text-xs text-yellow-700">
                        Ratio: {formData?.metaAspect?.toFixed(3)}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-yellow-200 rounded">
                      <h5 className="text-sm font-medium text-yellow-800 mb-1">Entered Dimensions</h5>
                      <p className="text-xs text-yellow-700">
                        {formData?.widthFt} × {formData?.heightFt}ft
                      </p>
                      <p className="text-xs text-yellow-700">
                        Ratio: {enteredAspect?.toFixed(3)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Video Duration Section for LED Screen */}
          {formData?.brandingType === 'LED Screen' && formData?.file?.type?.startsWith('video/') && (
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Video Duration</h3>
              <div className="space-y-4">
                {/* Auto-extracted Duration Display */}
                {formData?.durationSeconds && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Icon name="Clock" size={16} className="text-green-600" />
                      <span className="text-sm font-medium text-green-800">
                        Auto-detected Duration: {formatDuration(formData?.durationSeconds)}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Manual Override Input */}
                <div>
                  <Input
                    label="Manual Duration Override (seconds)"
                    type="number"
                    placeholder="e.g., 30.5"
                    value={formData?.durationOverrideSeconds}
                    onChange={(e) => handleInputChange('durationOverrideSeconds', e?.target?.value)}
                    error={errors?.durationOverrideSeconds}
                    min="0"
                    step="0.1"
                    helpText="Leave empty to use auto-detected duration"
                  />
                </div>
                
                {/* Final Duration Display */}
                {durationDisplay && (
                  <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Icon name="Play" size={16} className="text-primary" />
                      <span className="text-sm font-medium text-primary">Final Duration:</span>
                      <span className="text-lg font-bold text-primary font-mono">{durationDisplay}</span>
                    </div>
                  </div>
                )}
                
                {errors?.durationSeconds && (
                  <p className="text-sm text-yellow-600">{errors?.durationSeconds}</p>
                )}
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={isSubmitting}
              iconName="Upload"
              iconPosition="left"
            >
              Upload Visual
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadModal;